import { DayPicker as GammaDayPicker } from '@factorialco/deprecated-design-system'
import { DATE_FNS_LOCALES_MAPPING } from 'config/locales'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import {
  DayOfWeekIndex,
  FormatTypes,
  convertDate,
  dateToString,
  stringToDate,
} from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

type BaseProps = Omit<
  React.ComponentProps<typeof GammaDayPicker<FormatTypes, DayOfWeekIndex>>,
  | 'selectValue'
  | 'missingLabel'
  | 'onChange'
  | 'mode'
  | 'convertDate'
  | 'stringToDate'
  | 'dateToString'
  | 'format'
  | 'valueFormat'
> & {
  missingLabel?: string
  format?: FormatTypes
  valueFormat?: FormatTypes
}

type SingleProps = BaseProps & {
  mode?: 'single'
  value?: string
  onChange: (date: string) => void
}

type RangeProps = BaseProps & {
  mode: 'range'
  value?: [string | undefined, string | undefined]
  onChange: (date: [string | undefined, string | undefined]) => void
}

type Props = SingleProps | RangeProps

const isRangeProps = (props: Props): props is RangeProps => props.mode === 'range'

const FactorialDayPicker = GammaDayPicker<FormatTypes, DayOfWeekIndex>

const DayPicker = (props: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const commonProps = {
    selectValue: i18n.t('common.select'),
    missingLabel: props.missingLabel ?? i18n.t('common.missing_field'),
    convertDate,
    stringToDate,
    dateToString,
    format: props.format ?? 'ui',
    ...(locale && { locale: DATE_FNS_LOCALES_MAPPING[locale] }),
  }

  if (isRangeProps(props)) {
    return (
      <FactorialDayPicker
        {...props}
        {...commonProps}
        mode="range"
        valueFormat={props.valueFormat ?? 'backend'}
      />
    )
  }

  return (
    <FactorialDayPicker
      {...props}
      {...commonProps}
      mode="single"
      valueFormat={props.valueFormat ?? 'ui'}
    />
  )
}

export default DayPicker
